<script lang="ts">
  import { service } from '@frontend/services';
  import { GetCachedToken, me } from '@frontend/user';
  import { get } from 'svelte/store';
  import { onMount, onDestroy } from 'svelte';
  import { translate, currentLanguage } from "../Translator";
  import { push } from "svelte-spa-router";
  import type { GroupData, GameStatistics, User } from '@frontend/walletapi';


  function formatMoney(money: Money | undefined): string {
    if (!money || money.InCents === undefined) return '$0.00';
    const amount = money.InCents / 100; 
    return `$${Math.abs(amount).toFixed(2)}`;
  }


  function formatMoneyWithSign(money: Money | undefined): string {
    if (!money || money.InCents === undefined) return '$0.00';
    const amount = money.InCents / 100; 
    const sign = amount >= 0 ? '+' : '-';
    return `${sign}$${Math.abs(amount).toFixed(2)}`;
  }

  interface Grupo {
    Id: number;
    Name: string | undefined;
    ParentId: number | undefined;
    Users?: User[];
    level?: number;
  }

  type Money = import('@frontend/walletapi').Money;

  interface ResumenPorGrupo {
    [key: number]: GroupData;
  }

  // Inicializar fechas con el día actual
  let fechaInicio = new Date().toISOString().split('T')[0];
  let fechaFin = new Date().toISOString().split('T')[0];
  let loading = false;
  let error: string | null = null;
  let noResults = false;
  let grupos: Grupo[] = [];
  let gruposFiltrados: Grupo[] = [];
  let currentGroupId: number | null = null;
  let gruposSeleccionados: Set<number> = new Set();
  let todosSeleccionados = false;

  let resumenPorGrupo: ResumenPorGrupo = {};
  let menuVisible = false;
  let dropdownRef: HTMLElement | undefined;
  let showMoreColumns = false;
  let manualSelection = false;
  let hasInitialData = false;

  // Paginación para la tabla "Game Counters"
  let gameCounterPage = 1;
  const gameCounterPageSize = 20;


  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
      menuVisible = false;
    }
  }

  function toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    menuVisible = !menuVisible;
  }

  function handleItemClick(event: MouseEvent): void {
    event.stopPropagation(); 
  }

  onMount(async () => {
    try {
      if (!$me) {
        push("/user/Login");
        return;
      }

      document.addEventListener('click', handleClickOutside);


      let meStore = get(me);
      if (!meStore) {
     
        await new Promise(resolve => setTimeout(resolve, 1000));
        meStore = get(me);
        
        if (!meStore) {
          error = 'User information not available. Please refresh the page.';
          return;
        }
      }
      

      const gruposCargados = await cargarGrupos();
      if (!gruposCargados) return;
      

      if (meStore.Role === "Owner") {

        gruposFiltrados = getAllGroupsWithStructure(grupos);
      } else if (meStore.Role === "Admin" || meStore.Role === "Cashier") {
        
        if (!meStore.GroupId) {
          error = 'User does not have a group assigned';
          return;
        }
        
        currentGroupId = meStore.GroupId;
        

        filtrarGrupos();
      } else {
        error = 'User role not recognized';
        return;
      }

      todosSeleccionados = false;
      gruposSeleccionados = new Set<number>();

      // Auto-seleccionar todos los grupos disponibles por defecto
      if (gruposFiltrados.length > 0) {
        gruposFiltrados.forEach(grupo => {
          gruposSeleccionados.add(grupo.Id);
        });
        todosSeleccionados = true;
        
        // Ejecutar búsqueda automáticamente con todos los grupos
        setTimeout(() => {
          sumByDateDebit();
        }, 500);
      }
    } catch (err) {
      error = err instanceof Error ? err.message : 'Error initializing page';
    }
  });

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  $: {
    const meStore = get(me);
    
    if (meStore && grupos.length > 0) {
      
      if (meStore.Role === "Owner") {
        gruposFiltrados = getAllGroupsWithStructure(grupos);
      } else if (meStore.Role === "Admin" || meStore.Role === "Cashier") {
        if (!meStore.GroupId) {
          gruposFiltrados = [];
        } else {
          currentGroupId = meStore.GroupId;
          filtrarGrupos();
        }
      } else {
        gruposFiltrados = [];
      }
      
      // Auto-seleccionar todos los grupos disponibles cuando cambien los grupos filtrados
      if (gruposFiltrados.length > 0 && !manualSelection) {
        const newGruposSeleccionados = new Set<number>();
        gruposFiltrados.forEach(grupo => {
          newGruposSeleccionados.add(grupo.Id);
        });
        gruposSeleccionados = newGruposSeleccionados;
        todosSeleccionados = true;
      }
    }
  }

  function perteneceAJerarquia(grupo: Grupo, parentId: number): boolean {
    return grupo.ParentId === parentId;
  }

  function filtrarGrupos() {
    const meStore = get(me);
    
    if (meStore?.Role === "Owner") {
      gruposFiltrados = getAllGroupsWithStructure(grupos);
      return;
    }

    if (meStore?.Role !== "Admin" && meStore?.Role !== "Cashier") {
      gruposFiltrados = [];
      return;
    }

    if (!currentGroupId || !grupos.length) {
      gruposFiltrados = [];
      return;
    }

    const grupoActual = grupos.find(g => g.Id === currentGroupId);
    if (!grupoActual) {
      gruposFiltrados = [];
      return;
    }

    let gruposDisponibles: Grupo[] = [];

    if (meStore?.Role === "Cashier") {

      gruposDisponibles = [grupoActual];

      if (grupoActual.ParentId) {
        const grupoPadre = grupos.find(g => g.Id === grupoActual.ParentId);
        if (grupoPadre) {
          gruposDisponibles.unshift(grupoPadre); 
        }
      }
      
      gruposFiltrados = gruposDisponibles.map(g => ({ ...g, level: 0 }));
      
    } else if (meStore?.Role === "Admin") {
      gruposDisponibles = [grupoActual];
      
      function obtenerHijos(groupId: number): Grupo[] {
        const hijosDirectos = grupos.filter(g => g.ParentId === groupId);
        let todosLosHijos = [...hijosDirectos];
        
        hijosDirectos.forEach(hijo => {
          todosLosHijos = [...todosLosHijos, ...obtenerHijos(hijo.Id)];
        });
        
        return todosLosHijos;
      }
      
      const gruposDisponiblesCompletos = [
        ...gruposDisponibles,
        ...obtenerHijos(currentGroupId)
      ];
      
      gruposDisponibles = gruposDisponiblesCompletos;
    }

    if (meStore?.Role === "Admin") {
      gruposFiltrados = getAllGroupsWithStructure(gruposDisponibles);
    }

  }

  async function cargarGrupos() {
    try {
      const token = GetCachedToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await service.walletapi.groupList({
        Token: token
      });

      if (!response?.Success) {
        throw new Error('Error obtaining groups: ' + (response?.Message || 'Unknown error'));
      }

      if (!response.Data || !Array.isArray(response.Data)) {
        throw new Error('Group data is not in the expected format');
      }

      grupos = response.Data;

      return true;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Error loading groups';
      return false;
    }
  }

  async function sumByDateDebit() {
    try {
      if (!fechaInicio || !fechaFin) {
        error = 'Please select both dates';
        return;
      }

      if (gruposSeleccionados.size === 0) {
        error = 'Please select at least one group';
        return;
      }

      const meStore = get(me);
      if (!meStore) {
        error = 'User information not available';
        return;
      }

      const gruposPermitidos = new Set(gruposFiltrados.map(g => g.Id));
      const gruposNoPermitidos = Array.from(gruposSeleccionados).filter(id => !gruposPermitidos.has(id));
      
      if (gruposNoPermitidos.length > 0) {
        error = `You don't have permission to access some selected groups: ${gruposNoPermitidos.join(', ')}`;
        return;
      }

      error = null;
      loading = true;
      noResults = false;

      const token = GetCachedToken();
      if (!token) {
        error = 'No token available';
        return;
      }

      if (!meStore?.Id) {
        error = 'User ID not available';
        return;
      }

      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      fin.setHours(23, 59, 59);

      resumenPorGrupo = {};
      let hasError = false;
      let errorMessage = '';

        try {
        // Convertir Set a Array para enviar todos los IDs seleccionados
        const groupIdsArray = Array.from(gruposSeleccionados);
        
          const response = await service.walletapi.sumByDateDebit({
            Token: token,
            Data: {
              Datestart: inicio,
              Dateend: fin,
            GroupIds: groupIdsArray, // Enviar todos los IDs seleccionados
              TransactionType: undefined,
              PlayerId: undefined,
              GameId: undefined,
              UserId: meStore.Id,
              ParentId: undefined,
              PlayerSessionId: undefined
            }
          });

          if (response?.Success && response.Data) {
          // Manejar la nueva respuesta GroupedDataResponse con estructura GroupData[]
          const groupedData = response.Data;

          // Si es un array de GroupData
          if (Array.isArray(groupedData)) {
            // Obtener los juegos de Data.Totals.Games (consolidados)
            const allGames = response.Data?.Totals?.Games || [];

            (groupedData as GroupData[]).forEach((groupData) => {
              if (groupData.GroupId) {
                resumenPorGrupo[groupData.GroupId] = {
                  GroupId: groupData.GroupId,
                  GroupName: groupData.GroupName,
                  ParentId: groupData.ParentId,
                  ParentName: groupData.ParentName,
                  TotalDepositMoney: groupData.TotalDepositMoney,
                  TotalWithdrawMoney: groupData.TotalWithdrawMoney,
                  TotalDepositPromo: groupData.TotalDepositPromo,
                  TotalWithdrawPromo: groupData.TotalWithdrawPromo,
                  TotalDebitMoney: groupData.TotalDebitMoney,
                  TotalCreditMoney: groupData.TotalCreditMoney,
                  TotalEndRoundMoney: groupData.TotalEndRoundMoney,
                  TotalCancelBetMoney: groupData.TotalCancelBetMoney,
                  TotalDepositCancelMoney: groupData.TotalDepositCancelMoney,
                  TotalWithdrawCancelMoney: groupData.TotalWithdrawCancelMoney,
                  TotalWithdrawWaitingMoney: groupData.TotalWithdrawWaitingMoney,
                  TotalPromoCanceledMoney: groupData.TotalPromoCanceledMoney,
                  TotalPromoExpiredMoney: groupData.TotalPromoExpiredMoney,
                  Profit: groupData.Profit,
                  ProfitPercentage: groupData.ProfitPercentage,
                  Games: allGames // Usar los juegos consolidados de Totals
                };
              }
            });
          }
          // Si es un objeto con un array de GroupData
          else if (groupedData.Groups && Array.isArray(groupedData.Groups)) {
            // Obtener los juegos de Data.Totals.Games (consolidados)
            const allGames = response.Data?.Totals?.Games || [];

            groupedData.Groups.forEach((groupData) => {
              if (groupData.GroupId) {
                resumenPorGrupo[groupData.GroupId] = {
                  GroupId: groupData.GroupId,
                  GroupName: groupData.GroupName,
                  ParentId: groupData.ParentId,
                  ParentName: groupData.ParentName,
                  TotalDepositMoney: groupData.TotalDepositMoney,
                  TotalWithdrawMoney: groupData.TotalWithdrawMoney,
                  TotalDepositPromo: groupData.TotalDepositPromo,
                  TotalWithdrawPromo: groupData.TotalWithdrawPromo,
                  TotalDebitMoney: groupData.TotalDebitMoney,
                  TotalCreditMoney: groupData.TotalCreditMoney,
                  TotalEndRoundMoney: groupData.TotalEndRoundMoney,
                  TotalCancelBetMoney: groupData.TotalCancelBetMoney,
                  TotalDepositCancelMoney: groupData.TotalDepositCancelMoney,
                  TotalWithdrawCancelMoney: groupData.TotalWithdrawCancelMoney,
                  TotalWithdrawWaitingMoney: groupData.TotalWithdrawWaitingMoney,
                  TotalPromoCanceledMoney: groupData.TotalPromoCanceledMoney,
                  TotalPromoExpiredMoney: groupData.TotalPromoExpiredMoney,
                  Profit: groupData.Profit,
                  ProfitPercentage: groupData.ProfitPercentage,
                  Games: allGames // Usar los juegos consolidados de Totals
                };
              }
            });
          }
          // Si es un objeto con datos consolidados (no trae Groups[])
          else {
            // Obtener los juegos de Data.Totals.Games (consolidados)
            const allGames = response.Data?.Totals?.Games || [];
            const consolidated = groupedData as unknown as Partial<GroupData>;

            // Distribuir los datos a cada grupo seleccionado
            groupIdsArray.forEach(groupId => {
              resumenPorGrupo[groupId] = {
                GroupId: groupId,
                GroupName: undefined,
                ParentId: consolidated.ParentId,
                ParentName: undefined,
                TotalDepositMoney: consolidated.TotalDepositMoney ?? { InCents: 0 },
                TotalWithdrawMoney: consolidated.TotalWithdrawMoney ?? { InCents: 0 },
                TotalDepositPromo: consolidated.TotalDepositPromo ?? { InCents: 0 },
                TotalWithdrawPromo: consolidated.TotalWithdrawPromo ?? { InCents: 0 },
                TotalDebitMoney: consolidated.TotalDebitMoney ?? { InCents: 0 },
                TotalCreditMoney: consolidated.TotalCreditMoney ?? { InCents: 0 },
                TotalEndRoundMoney: consolidated.TotalEndRoundMoney ?? { InCents: 0 },
                TotalCancelBetMoney: consolidated.TotalCancelBetMoney ?? { InCents: 0 },
                TotalDepositCancelMoney: consolidated.TotalDepositCancelMoney ?? { InCents: 0 },
                TotalWithdrawCancelMoney: consolidated.TotalWithdrawCancelMoney ?? { InCents: 0 },
                TotalWithdrawWaitingMoney: consolidated.TotalWithdrawWaitingMoney ?? { InCents: 0 },
                TotalPromoCanceledMoney: consolidated.TotalPromoCanceledMoney ?? { InCents: 0 },
                TotalPromoExpiredMoney: consolidated.TotalPromoExpiredMoney ?? { InCents: 0 },
                Profit: consolidated.Profit,
                ProfitPercentage: consolidated.ProfitPercentage ?? 0,
                Games: allGames // Usar los juegos consolidados de Totals
              };
            });
          }

          } else {
            hasError = true;
            if (response?.ErrorMessage) {
              errorMessage = response.ErrorMessage;
            } else {
              errorMessage = 'No valid data received from API';
            }
          
          // Crear datos vacíos para cada grupo seleccionado
          groupIdsArray.forEach(groupId => {
            resumenPorGrupo[groupId] = {
              TotalDepositMoney: { InCents: 0 },
              TotalWithdrawMoney: { InCents: 0 },
              TotalDepositPromo: { InCents: 0 },
              TotalWithdrawPromo: { InCents: 0 },
              TotalDebitMoney: { InCents: 0 },
              TotalCreditMoney: { InCents: 0 },
              TotalEndRoundMoney: { InCents: 0 },
              TotalCancelBetMoney: { InCents: 0 },
              TotalDepositCancelMoney: { InCents: 0 },
              TotalWithdrawCancelMoney: { InCents: 0 },
              TotalWithdrawWaitingMoney: { InCents: 0 },
              TotalPromoCanceledMoney: { InCents: 0 },
              TotalPromoExpiredMoney: { InCents: 0 },
              GroupId: groupId,
              GroupName: undefined,
              ParentId: undefined,
              ParentName: undefined,
              Profit: undefined,
              ProfitPercentage: 0,
              Games: []
            };
          });
          }
        } catch (err) {
          hasError = true;
          errorMessage = 'Error obtaining transaction summary';

        // Crear datos vacíos para cada grupo seleccionado
        Array.from(gruposSeleccionados).forEach(groupId => {
          resumenPorGrupo[groupId] = {
            TotalDepositMoney: { InCents: 0 },
            TotalWithdrawMoney: { InCents: 0 },
            TotalDepositPromo: { InCents: 0 },
            TotalWithdrawPromo: { InCents: 0 },
            TotalDebitMoney: { InCents: 0 },
            TotalCreditMoney: { InCents: 0 },
            TotalEndRoundMoney: { InCents: 0 },
            TotalCancelBetMoney: { InCents: 0 },
            TotalDepositCancelMoney: { InCents: 0 },
            TotalWithdrawCancelMoney: { InCents: 0 },
            TotalWithdrawWaitingMoney: { InCents: 0 },
            TotalPromoCanceledMoney: { InCents: 0 },
            TotalPromoExpiredMoney: { InCents: 0 },
            GroupId: groupId,
            GroupName: undefined,
            ParentId: undefined,
            ParentName: undefined,
            Profit: undefined,
            ProfitPercentage: 0,
            Games: []
          };
        });
      }

      if (hasError) {
        error = errorMessage;
      } else {
        error = null;
      }

      hasInitialData = true;

    } catch (err) {
      error = 'Error obtaining transaction summary';
    } finally {
      loading = false;
    }
  }

  function toggleGrupoButton(id: number): void {
    manualSelection = true; 
    
    const gruposPermitidos = new Set(gruposFiltrados.map(g => g.Id));
    if (!gruposPermitidos.has(id)) {
      return;
    }
    
    const newGruposSeleccionados = new Set<number>(gruposSeleccionados);
    const grupoActual = grupos.find(g => g.Id === id);
    
    if (!grupoActual) return;

    if (newGruposSeleccionados.has(id)) {
      newGruposSeleccionados.delete(id);
    } else {
      newGruposSeleccionados.add(id);
    }

    gruposSeleccionados = newGruposSeleccionados;
    
    setTimeout(() => {
      manualSelection = false;
    }, 100);
  }

  function toggleTodosButton(): void {
    manualSelection = true; 
    
    if (todosSeleccionados) {
      gruposSeleccionados = new Set<number>();
    } else {
      const newGruposSeleccionados = new Set<number>();
      gruposFiltrados.forEach(grupo => {
        newGruposSeleccionados.add(grupo.Id);
      });
      gruposSeleccionados = newGruposSeleccionados;
    }
    
    gruposSeleccionados = gruposSeleccionados;
    
    setTimeout(() => {
      manualSelection = false;
    }, 100);
  }


  function handleCheckboxChange(event: Event, id: number): void {
    event.stopPropagation();
    toggleGrupoButton(id);
  }


  function handleSelectAll(event: Event): void {
    event.stopPropagation(); 
    toggleTodosButton();
  }

  $: {
    if (gruposFiltrados.length > 0 && !manualSelection) {
      todosSeleccionados = gruposFiltrados.every(grupo => gruposSeleccionados.has(grupo.Id));
    }
  }

  $: {
    if (currentGroupId) {
      filtrarGrupos();
    }
  }

  // Reactive statement para ejecutar búsqueda automática cuando cambien los grupos seleccionados
  // Comentado para evitar conflictos con las funciones de selección
  /*
  $: {
    if (hasInitialData && gruposSeleccionados.size > 0 && !manualSelection && fechaInicio && fechaFin) {
      // Usar setTimeout para evitar múltiples ejecuciones
      setTimeout(() => {
        sumByDateDebit();
      }, 100);
    }
  }
  */

  
  function getAllGroupsWithStructure(grupos: Grupo[]): Grupo[] {
    const result: Grupo[] = [];
    
    function addGroup(grupo: Grupo, level: number = 0) {
      const grupoConNivel = { ...grupo, level };
      result.push(grupoConNivel);
      
      const children = grupos.filter(g => g.ParentId && g.ParentId === grupo.Id);
      // Ordenar los hijos por nombre si es necesario
      children.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
      children.forEach(child => addGroup(child, level + 1));
    }


    const parentGroups = grupos.filter(g => !g.ParentId);
    parentGroups.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
    parentGroups.forEach(grupo => addGroup(grupo));
    
    return result;
  }

  function getGroupDisplayName(grupo: Grupo): string {
    const meStore = get(me);

    if (grupo.Id === currentGroupId) {
      return `📁 ${grupo.Name || ''}`;
    }

    if (meStore?.Role === "Cashier" && meStore?.GroupId && grupo.Id === grupos.find(g => g.Id === meStore.GroupId)?.ParentId) {
      return `⬆️ ${grupo.Name || ''}`;
    }
    

    if (meStore?.Role === "Admin" && grupo.ParentId === currentGroupId) {
      return `└─ ${grupo.Name || ''}`;
    }
  
    if (grupo.level && grupo.level > 0) {

      if (grupo.level === 1) {
        return `└─ ${grupo.Name || ''}`;
      }
      
    
      const spaces = '   '.repeat(grupo.level - 1);
      return `${spaces}└─ ${grupo.Name || ''}`;
    }
    
    return grupo.Name || '';
  }

  function getUserRoleDisplayName(role: string): string {
    switch (role) {
      case 'Owner':
        return 'Owner (All Groups)';
      case 'Admin':
        return 'Admin (Assigned Group + Subgroups)';
      case 'Cashier':
        return 'Cashier (Assigned Group + Parent Group)';
      default:
        return role;
    }
  }


  $: {
    if (grupos.length > 0) {
      const meStore = get(me);
      if (meStore?.Role === "Owner") {
        gruposFiltrados = getAllGroupsWithStructure(grupos);
      } else if (meStore?.Role === "Admin" || meStore?.Role === "Cashier") {
        if (currentGroupId) {
          filtrarGrupos();
        }
      }
      
      // Auto-seleccionar todos los grupos disponibles cuando cambien los grupos filtrados
      if (gruposFiltrados.length > 0 && !manualSelection) {
        const newGruposSeleccionados = new Set<number>();
        gruposFiltrados.forEach(grupo => {
          newGruposSeleccionados.add(grupo.Id);
        });
        gruposSeleccionados = newGruposSeleccionados;
        todosSeleccionados = true;
      }
    }
  }

  type GameCounterRow = { grupo: Grupo; game: GameStatistics | null; isFirstOfGroup: boolean };

  $: gameCounterRows = grupos
    .filter(grupo => gruposSeleccionados.has(grupo.Id) && resumenPorGrupo[grupo.Id])
    .flatMap((grupo): GameCounterRow[] => {
      const resumen = resumenPorGrupo[grupo.Id];
      if (resumen.Games && resumen.Games.length > 0) {
        return resumen.Games.map((game, index) => ({ grupo, game, isFirstOfGroup: index === 0 }));
      }
      return [{ grupo, game: null, isFirstOfGroup: true }];
    });

  $: gameCounterTotalPages = Math.max(1, Math.ceil(gameCounterRows.length / gameCounterPageSize));

  $: if (gameCounterPage > gameCounterTotalPages) {
    gameCounterPage = gameCounterTotalPages;
  }

  $: paginatedGameCounterRows = gameCounterRows
    .slice((gameCounterPage - 1) * gameCounterPageSize, gameCounterPage * gameCounterPageSize)
    .map((row, i, slice) => ({
      ...row,
      isFirstOfGroup: i === 0 || slice[i - 1].grupo.Id !== row.grupo.Id
    }));

  $: {
    resumenPorGrupo;
    gameCounterPage = 1;
  }

  function goToGameCounterPage(page: number) {
    if (page >= 1 && page <= gameCounterTotalPages) {
      gameCounterPage = page;
    }
  }
</script>

<div class="ui container">

  <!-- Información de permisos del usuario -->
  <div class="ui segment">
    <h3 class="ui header">{$translate('Sums')}</h3>
    {#if get(me)}
      <!-- {@const meStore = get(me)}
      <div class="ui info message">
        <div class="header">Current User: {meStore?.FirstName} {meStore?.LastName}</div>
        <p><strong>Role:</strong> {getUserRoleDisplayName(meStore?.Role || '')}</p>
        {#if meStore?.GroupId}
          <p><strong>Assigned Group ID:</strong> {meStore.GroupId}</p>
        {/if}
        <p><strong>Available Groups:</strong> {gruposFiltrados.length}</p>
        <p><strong>Selected Groups:</strong> {gruposSeleccionados.size}</p> -->
        <!-- {#if meStore?.Role === 'Cashier'}
          <div class="ui warning message" style="margin-top: 1em;">
            <div class="header">{$translate('Available Groups')}</div>
            <p>{$translate('You can see players from your group, parent group, and subgroups.')}</p>
          </div>
        {/if} -->
      <!-- </div> -->
    {/if}
  </div>

  <div class="ui segment">
    <div class="ui form">
      <div class="fields">
        <div class="field">
          <label>{$translate('Start Date')}</label>
          <div class="ui input">
            <input 
              type="date" 
              bind:value={fechaInicio}
              max={fechaFin}
            >
          </div>
        </div>
        <div class="field">
          <label>{$translate('End Date')}</label>
          <div class="ui input">
            <input 
              type="date" 
              bind:value={fechaFin}
              min={fechaInicio}
            >
          </div>
        </div>
        <div class="field">
          <label>{$translate('Groups')}</label>
          <div 
            class="ui fluid multiple search selection dropdown" 
            style="width: 100%; margin-bottom: 1em; position: relative;"
            bind:this={dropdownRef}
            on:click={toggleMenu}
          >
            <input type="hidden" name="groups">
            <i class="dropdown icon"></i>
            <div class="default text" style="font-size: 0.9em;">{$translate('Multiple groups')} ({gruposSeleccionados.size})</div>
            <div class="menu" class:visible={menuVisible}>
              <div class="item" on:click={handleSelectAll}>
                <div class="ui checkbox">
                  <input 
                    type="checkbox" 
                    checked={todosSeleccionados}
                  >
                </div>
                <span style="font-size: 0.9em;">{$translate('Select All')}</span>
              </div>
              {#each gruposFiltrados as grupo}
                <div 
                  class="item"
                  class:selected={gruposSeleccionados.has(grupo.Id)}
                  style="display: flex; align-items: center; padding: 0.4em 0.8em;"
                  on:click={handleItemClick}
                >
                  <div class="ui checkbox">
                    <input 
                      type="checkbox" 
                      checked={gruposSeleccionados.has(grupo.Id)}
                      on:change={(e) => handleCheckboxChange(e, grupo.Id)}
                    >
                  </div>
                  <span style="flex-grow: 1; white-space: pre; font-size: 0.9em;">
                    {getGroupDisplayName(grupo)}
                    {#if grupo.Id === currentGroupId}
                      <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                        {$translate('Current')}
                      </div>
                    {/if}
                  </span>
                </div>
              {/each}
            </div>
          </div>
        </div>
        <div class="field search-button-field">
          <label>&nbsp;</label>
          <button 
            class="ui primary button large fluid"
            on:click={sumByDateDebit}
            disabled={loading || !fechaInicio || !fechaFin || gruposSeleccionados.size === 0}
          >
            {#if loading}
              <i class="spinner loading icon"></i> {$translate('Loading')}...
            {:else}
              <i class="search icon"></i> {$translate('Search')}
            {/if}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="ui segment">
  {#if error}
    <div class="ui negative message">
      <p>{error}</p>
    </div>
  {/if}
      {#if noResults}
        <div class="ui warning message">
          <div class="header"> {$translate('No results found')}</div>
          <p> {$translate('No data available for the selected period')}.</p>
        </div>
      {/if}
      
      <!-- Tabla principal de resumen -->
      <div class="ui header" style="display: flex; justify-content: space-between; align-items: center;">
        <h3 class="content">{$translate('Transaction Summary')}</h3>
        <button 
          class="ui small button"
          on:click={() => showMoreColumns = !showMoreColumns}
        >
          {#if showMoreColumns}
            <i class="chevron up icon"></i> {$translate('Less')}
          {:else}
            <i class="chevron down icon"></i> {$translate('More')}
          {/if}
        </button>
      </div>
      <table class="ui celled table">
        <thead>
          <tr>
            <th>{$translate('Groups')}</th>
            <th>{$translate('Deposit Total')}</th>
            <th>{$translate('Deposit Promo')}</th>
            <th>{$translate('Withdraw')}</th>
            <th>{$translate('Deposit Profit')}</th>
            {#if showMoreColumns}
              <th>{$translate('Bet')}</th>
              <th>{$translate('Win')}</th>
              <th>{$translate('Profit')}</th>
              <th>{$translate('End Round')}</th>
              <th>{$translate('Cancel Bet')}</th>
              <th>{$translate('Deposit Cancel')}</th>
              <th>{$translate('Withdraw Cancel')}</th>
            {/if}
          </tr>
        </thead>
        <tbody>
          {#each grupos as grupo}
            {#if gruposSeleccionados.has(grupo.Id)}
              {@const resumen = resumenPorGrupo[grupo.Id]}
              <tr>
                <td>
                  <span class="group-name">
                    {grupo.Name}
                    {#if grupo.Id === currentGroupId}
                      <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                        {$translate('Current')}
                      </div>
                    {/if}
                  </span>
                </td>
                <td class="positive">{formatMoney(resumen?.TotalDepositMoney)}</td>
                <td class="positive">{formatMoney(resumen?.TotalDepositPromo)}</td>
                <td class="negative">{formatMoney(resumen?.TotalWithdrawMoney)}</td>
                <td class:negative={(resumen?.TotalDepositMoney?.InCents || 0) - (resumen?.TotalWithdrawMoney?.InCents || 0) < 0}
                    class:positive={(resumen?.TotalDepositMoney?.InCents || 0) - (resumen?.TotalWithdrawMoney?.InCents || 0) > 0}>
                  {formatMoneyWithSign({InCents: (resumen?.TotalDepositMoney?.InCents || 0) - (resumen?.TotalWithdrawMoney?.InCents || 0)})}
                </td>
                {#if showMoreColumns}
                  <td>{formatMoney(resumen?.TotalDebitMoney)}</td>
                  <td>{formatMoney(resumen?.TotalCreditMoney)}</td>
                  <td class:negative={(resumen?.TotalDebitMoney?.InCents || 0) - (resumen?.TotalCreditMoney?.InCents || 0) < 0}
                      class:positive={(resumen?.TotalDebitMoney?.InCents || 0) - (resumen?.TotalCreditMoney?.InCents || 0) >= 0}>
                    {formatMoneyWithSign({InCents: (resumen?.TotalDebitMoney?.InCents || 0) - (resumen?.TotalCreditMoney?.InCents || 0)})}
                  </td>
                  <td>{formatMoney(resumen?.TotalEndRoundMoney)}</td>
                  <td>{formatMoney(resumen?.TotalCancelBetMoney)}</td>
                  <td class="warning">{formatMoney(resumen?.TotalDepositCancelMoney)}</td>
                  <td class="warning">{formatMoney(resumen?.TotalWithdrawCancelMoney)}</td>
                {/if}
              </tr>
            {/if}
          {/each}
          {#if gruposSeleccionados.size > 0}
            {@const totals = Array.from(gruposSeleccionados).reduce((acc, groupId) => {
              const resumen = resumenPorGrupo[groupId];
              return {
                deposit: acc.deposit + (resumen?.TotalDepositMoney?.InCents || 0),
                withdraw: acc.withdraw + (resumen?.TotalWithdrawMoney?.InCents || 0),
                bet: acc.bet + (resumen?.TotalDebitMoney?.InCents || 0),
                win: acc.win + (resumen?.TotalCreditMoney?.InCents || 0),
                depositPromo: acc.depositPromo + (resumen?.TotalDepositPromo?.InCents || 0),
                endRound: acc.endRound + (resumen?.TotalEndRoundMoney?.InCents || 0),
                cancelBet: acc.cancelBet + (resumen?.TotalCancelBetMoney?.InCents || 0),
                depositCancel: acc.depositCancel + (resumen?.TotalDepositCancelMoney?.InCents || 0),
                withdrawCancel: acc.withdrawCancel + (resumen?.TotalWithdrawCancelMoney?.InCents || 0)
              };
            }, {
              deposit: 0,
              withdraw: 0,
              bet: 0,
              win: 0,
              depositPromo: 0,
              endRound: 0,
              cancelBet: 0,
              depositCancel: 0,
              withdrawCancel: 0
            })}
            <tr class="total-row">
              <td><strong>{$translate('Total')}</strong></td>
              <td class="positive"><strong>{formatMoney({InCents: totals.deposit})}</strong></td>
              <td class="positive"><strong>{formatMoney({InCents: totals.depositPromo})}</strong></td>
              <td class="negative"><strong>{formatMoney({InCents: totals.withdraw})}</strong></td>
              <td class:negative={totals.deposit - totals.withdraw < 0}
                  class:positive={totals.deposit - totals.withdraw > 0}>
                <strong>{formatMoneyWithSign({InCents: totals.deposit - totals.withdraw})}</strong>
              </td>
              {#if showMoreColumns}
                <td><strong>{formatMoney({InCents: totals.bet})}</strong></td>
                <td><strong>{formatMoney({InCents: totals.win})}</strong></td>
                <td class:negative={totals.bet - totals.win < 0}
                    class:positive={totals.bet - totals.win >= 0}>
                  <strong>{formatMoneyWithSign({InCents: totals.bet - totals.win})}</strong>
                </td>
                <td><strong>{formatMoney({InCents: totals.endRound})}</strong></td>
                <td><strong>{formatMoney({InCents: totals.cancelBet})}</strong></td>
                <td class="warning"><strong>{formatMoney({InCents: totals.depositCancel})}</strong></td>
                <td class="warning"><strong>{formatMoney({InCents: totals.withdrawCancel})}</strong></td>
              {/if}
            </tr>
          {/if}
        </tbody>
      </table>
      
      {#if gruposSeleccionados.size === 0}
        <div class="ui info message" style="margin-top: 1em;">
          <div class="header">{$translate('No groups selected')}</div>
          <p>{$translate('Please select one or more groups to view transaction data')}.</p>
        </div>
      {/if}

      <!-- Nueva tabla para datos del juego -->
      <h3 class="ui header">
        <div class="content">
          {$translate('Game Counters')}
        </div>
      </h3>
      <div class="ui segment">
        <table class="ui celled table game-counters">
        <thead>
          <tr>
              <th class="group-column">{$translate('Group Name')}</th>
              <th class="game-column">  {$translate('Game Name')}</th>
              <th class="number-column">{$translate('Total Rounds')}</th>
              <th class="number-column">{ $translate('Total Players')}</th>
          </tr>
        </thead>
        <tbody>
            {#each paginatedGameCounterRows as row}
              <tr class:highlight={row.grupo.Id === currentGroupId}>
                <td class="group-column">
                  {#if row.isFirstOfGroup}
                    <div class="group-header">
                      {row.grupo.Name}
                      {#if row.grupo.Id === currentGroupId}
                        <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                          {$translate('Current')}
                        </div>
                      {/if}
                    </div>
                  {/if}
                </td>
                {#if row.game}
                  <td class="game-column">{row.game.GameName || 'N/A'}</td>
                  <td class="number-column">{row.game.TotalJugadas || 0}</td>
                  <td class="number-column">{row.game.TotalJugadores || 0}</td>
                {:else}
                  <td colspan="3" class="no-data">{$translate('No games data available')}</td>
                {/if}
              </tr>
            {/each}
        </tbody>
      </table>

      {#if gameCounterTotalPages > 1}
        <div class="ui pagination menu" style="justify-content: center; margin-top: 1em;">
          <a class="icon item" class:disabled={gameCounterPage === 1} on:click={() => goToGameCounterPage(gameCounterPage - 1)}>
            <i class="left chevron icon"></i>
          </a>
          <div class="item">
            {$translate('Page')} {gameCounterPage} {$translate('of')} {gameCounterTotalPages}
          </div>
          <a class="icon item" class:disabled={gameCounterPage === gameCounterTotalPages} on:click={() => goToGameCounterPage(gameCounterPage + 1)}>
            <i class="right chevron icon"></i>
          </a>
        </div>
      {/if}

      {#if gruposSeleccionados.size === 0}
        <div class="ui info message" style="margin-top: 1em;">
          <div class="header">{$translate('No groups selected')}</div>
          <p>{$translate('Please select one or more groups to view game data')}.</p>
        </div>
      {/if}
      </div>

      <!-- Tabla de resumen de PromotionTransaction -->
      <h3 class="ui header">
        <div class="content">{$translate('Promotion Transactions Summary')}</div>
      </h3>
      <table class="ui celled table">
        <thead>
          <tr>
            <th>{$translate('Groups')}</th>
            <th>{$translate('Deposit Promo')}</th>
            <th>{$translate('Withdraw Promo')}</th>
          </tr>
        </thead>
        <tbody>
          {#each grupos as grupo}
            {#if gruposSeleccionados.has(grupo.Id)}
              {@const resumen = resumenPorGrupo[grupo.Id]}
              <tr>
                <td>
                  <span class="group-name">
                    {grupo.Name}
                    {#if grupo.Id === currentGroupId}
                      <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                        {$translate('Current')}
                      </div>
                    {/if}
                  </span>
                </td>
                <td>{formatMoney(resumen?.TotalDepositPromo)}</td>
                <td>{formatMoney(resumen?.TotalWithdrawPromo)}</td>
              </tr>
            {/if}
          {/each}
        </tbody>
      </table>
      
      {#if gruposSeleccionados.size === 0}
        <div class="ui info message" style="margin-top: 1em;">
          <div class="header">{$translate('No groups selected')}</div>
          <p>{$translate('Please select one or more groups to view promotion data')}.</p>
        </div>
      {/if}
    </div>
</div>

<style>
   .ui.dropdown {
    min-height: 2.2em !important;
    position: relative;
    padding: 0.4em 0.8em;
    cursor: pointer;
    z-index: 1000;
  }

  .ui.dropdown .menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: 0.28571429rem;
    box-shadow: 0 2px 4px 0 rgba(34,36,38,.15);
    z-index: 1001;
    max-height: 250px;
    overflow-y: auto;
    margin-top: 0.3em !important;
    width: 100%;
  }

  .ui.dropdown .menu > .item {
    position: relative;
    z-index: 1002;
    background: white;
    padding: 0.4em 0.8em !important;
    line-height: 1.2;
  }

  .ui.dropdown .menu > .item:hover {
    background: rgba(0,0,0,.05);
  }

  .ui.dropdown .menu > .item.selected {
    background: rgba(0,0,0,.03) !important;
  }

  .ui.dropdown .text {
    margin-right: 2em;
    line-height: 1.2;
    display: flex !important;
    align-items: center !important;
  }

  .ui.checkbox {
    margin-right: 8px !important;
    min-width: 14px !important;
  }

  .ui.checkbox input[type="checkbox"] {
    width: 14px !important;
    height: 14px !important;
  }

  .ui.dropdown .menu > .item span {
    font-family: monospace;
    padding: 0.2em 0;
  }

  .ui.dropdown .menu.visible {
    display: block;
  }

  /* Ajustar el tamaño del contenedor del dropdown */
  .field .ui.dropdown {
    min-width: 200px;
    max-width: 250px;
  }

  .ui.table {
    position: relative;
    z-index: 1;
  }

  .ui.checkbox {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    margin: 0 !important;
    margin-right: 10px !important;
    min-width: 16px !important;
  }

  .ui.checkbox input[type="checkbox"] {
    position: relative !important;
    margin: 0 !important;
    width: 16px !important;
    height: 16px !important;
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
    z-index: 1 !important;
    -webkit-appearance: checkbox !important;
    appearance: checkbox !important;
    cursor: pointer !important;
  }

  /* Estilos de los items del menú */
  .ui.dropdown .menu > .item {
    padding: 0.5em 1em !important;
    display: flex !important;
    align-items: center !important;
    cursor: pointer;
  }

  .ui.dropdown .menu > .item:hover {
    background: rgba(0,0,0,.05);
  }

  .ui.dropdown .menu > .item.selected {
    background: rgba(0,0,0,.03) !important;
  }

  /* Etiquetas */
  .ui.mini.label {
    margin-left: 0.5em;
    padding: 0.2em 0.5em;
    font-size: 0.7em;
    vertical-align: middle;
    display: inline-block;
    background: #e8e8e8;
    border-radius: 0.28571429rem;
  }

  /* Estilos de los inputs de fecha */
  .ui.input input[type="date"] {
    padding: 0.5em;
    font-size: 1em;
    width: 100%;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: 0.28571429rem;
  }

  /* Centrar textos de la tabla */
  .ui.table th,
  .ui.table td {
    text-align: center !important;
  }

  /* Mantener alineación izquierda para la columna de grupo */
  .ui.table td:first-child {
    text-align: left !important;
  }

  /* Estilos para la tabla de Game Counters */
  .game-counters {
    margin-top: 1em;
  }

  .game-counters th {
    background-color: #f8f9fa !important;
    font-weight: 600 !important;
  }

  .game-counters .group-column {
    width: 25% !important;
    text-align: left !important;
  }

  .game-counters .game-column {
    width: 35% !important;
    text-align: left !important;
  }

  .game-counters .number-column {
    width: 20% !important;
    text-align: right !important;
  }

  .game-counters .group-header {
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-weight: 500;
  }

  .game-counters .no-data {
    text-align: center !important;
    color: #999;
    font-style: italic;
  }

  .game-counters tr.highlight {
    background-color: #f8f9fa;
  }

  .game-counters tr.highlight td {
    background-color: #f8f9fa !important;
  }

  .game-counters .ui.mini.label {
    background-color: #e8e8e8;
    color: #666;
    font-weight: 500;
  }

  .ui.pagination.menu .item {
    cursor: pointer;
  }

  .ui.pagination.menu .item.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  /* Estilos para el indicador de grupo actual */
  .ui.mini.label {
    margin-left: 0.5em;
    padding: 0.2em 0.5em;
    font-size: 0.7em;
    vertical-align: middle;
    display: inline-block;
    border-radius: 0.28571429rem;
  }

  /* Estilos para el nombre del grupo */
  .group-name {
    display: flex;
    align-items: center;
    font-weight: 500;
  }

  /* Estilos para el header del grupo en la tabla de juegos */
  .group-header {
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-weight: 500;
  }

  /* Estilos para el mensaje de información del usuario */


</style>